import { Box, Card, Chip, Divider, Typography, useTheme } from '@mui/material';
import { DateRange, Gavel } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { Room } from '@bandido/shared/types';
import dayjs from 'dayjs';

interface HotelPropertyFareCardProps {
  propertyName: string;
  rooms: Room[];
  boardDescription: string;
  checkinDate: string;
  checkoutDate: string;
  rateRules: string[];
  cancelPolicy: {
    refundable: boolean;
    description?: string;
  };
  supplierName: string;
  currency: string;
  price: number;
  onSelectFare?: () => void;
}

export const HotelPropertyFareCard = ({
  propertyName,
  rooms,
  boardDescription,
  checkinDate,
  checkoutDate,
  rateRules,
  cancelPolicy,
  supplierName,
  currency,
  price,
  onSelectFare,
}: HotelPropertyFareCardProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const formatDate = (date: string) => dayjs(date).format('DD MMM YYYY - HH:mm a');

  return (
    <Box width="100%">
      <Card sx={{ p: theme.spacing(3) }} elevation={4}>
        <Box
          display="flex"
          gap={3}
          width="100%"
          minHeight="200px"
          alignItems="stretch"
        >
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            gap={3}
            justifyContent="space-between"
          >
            <Box display="flex" gap={1}>
              <Typography variant="h6">
                {propertyName}
              </Typography>
            </Box>

            {/* ROOMS */}
            <Box
              gap={1}
              display="flex"
              flexDirection="column"
              alignSelf="stretch"
            >
              {rooms.map((room, idx) => (
                <Box key={idx} display="flex" alignItems="center" gap={1}>
                  <Typography variant="subtitle2">
                    1x {room.description}
                  </Typography>

                  <Typography variant="body2" color={theme.palette.text.disabled}>
                    {room.paxes} pax
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Chip size='medium' label={boardDescription} />
            </Box>

            {/* DETAILS */}
            <Box
              display="flex"
              flexDirection="column"
              gap={1}
            >
              {/* CHECKING / CHECKOUT */}
              <Box
                display="flex"
                gap={2}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <DateRange htmlColor={theme.palette.text.disabled} />
                  <Typography variant="subtitle2">{t('hotels.fares.checkIn')}: </Typography>
                  <Typography variant="body2" color={theme.palette.text.disabled}>{formatDate(checkinDate)}</Typography>
                </Box>
                <Divider orientation="vertical" />
                <Box display="flex" alignItems="center" gap={1}>
                  <DateRange htmlColor={theme.palette.text.disabled} />
                  <Typography variant="subtitle2">{t('hotels.fares.checkOut')}: </Typography>
                  <Typography variant="body2" color={theme.palette.text.disabled}>{formatDate(checkoutDate)}</Typography>
                </Box>
              </Box>

              {/* PAYMENT DETAILS */}
              <Box display="flex" gap={1}>
                <Gavel htmlColor={theme.palette.text.disabled} />
                <Typography variant="subtitle2">{t('hotels.fares.rateRules.title')}</Typography>
                {rateRules.length > 0 ? rateRules.map((rateRule) => (
                  <Chip
                    key={rateRule}
                    label={rateRule}
                    size='small'
                  />
                )) : 'N/A'}
              </Box>

              <Box display="flex" gap={1} justifyContent="space-between" alignItems="center">
                {/* CANCELATION POLICY */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip 
                    label={cancelPolicy.refundable ? 'Refundable' : 'Non-refundable'} 
                    color={cancelPolicy.refundable ? 'success' : 'error'}
                    size="small"
                  />
                  {cancelPolicy.description && (
                    <Typography variant="caption" color={theme.palette.text.disabled}>
                      {cancelPolicy.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem />

          <Box display="flex" flexDirection="column" justifyContent="space-between" alignItems="center" minWidth="200px" >
            <Box width="100%" display="flex" flexDirection="row-reverse">
              <Chip label={supplierName} color="secondary" />
            </Box>

            <Box display="flex" gap={1} alignItems="center" flexDirection="column">
              <Typography variant="h6" fontWeight={700} color="primary">
                {currency} {price.toFixed(2)}
              </Typography>
              <Typography 
                variant="caption" 
                color="primary" 
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => console.log('Show price details')}
              >
                {t('hotels.cancelPolicy.details')}
              </Typography>
            </Box>

            <Box display="flex" gap={1} width="100%">
              {onSelectFare && (
                <LoadingButton
                  onClick={onSelectFare}
                  color="primary"
                  variant="contained"
                  fullWidth
                >
                  {t('common.continue')}
                </LoadingButton>
              )}
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};
